import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import clsx from "clsx";
import { toast } from "react-toastify";
import { useHeritageAuthStatus } from "../../../../hooks/useHeritageAuthStatus";
import {
    HERITAGE_REFRESH_STOCK_BRANCH_SEND_MISSING,
    HERITAGE_SEND_CATALOG_BRANCH_HINT,
} from "../../../vendors/components/heritageCatalogBranchGuidance";
import { heritageApiErrorMessage } from "../../../vendors/components/heritageApiErrorMessage";
import { resolveSendBranch, groupSendBranches } from "./sendBranchResolution";
import { sendPurchaseOrderActionLabel } from "./sendPurchaseOrderLabels";
import {
    credentialsBlockMessage,
    isHeritageCredentialsError,
    partitionStockResults,
    sellabilityBlockMessage,
} from "./sendStockResults";

function heritageSendApiDisabledHint(
    auth: {
        connected?: boolean;
        hasCredentials?: boolean;
        tokenExpired?: boolean;
        tokenMissing?: boolean;
        connectionState?: string;
        token?: { isExpired?: boolean } | null;
    } | undefined
): { message: string; title: string } {
    if (!auth?.hasCredentials || auth.connectionState === "credentials_missing") {
        return {
            message: "(Heritage credentials missing)",
            title: "Add Heritage Customer Account ID and API key in Vendors → Edit Vendor for this Heritage vendor.",
        };
    }
    if (auth.connectionState === "token_expired" || auth.tokenExpired || auth.token?.isExpired) {
        return {
            message: "(Heritage token expired)",
            title: "Heritage login expired. Credentials are still saved — open Edit Vendor for this Heritage vendor and click Connect to refresh the token. Catalog data is kept.",
        };
    }
    if (auth.connectionState === "token_missing" || auth.tokenMissing) {
        return {
            message: "(Heritage not connected)",
            title: "Heritage credentials are saved but the company is not connected. Open Edit Vendor for the Heritage vendor and click Connect.",
        };
    }
    return {
        message: "(Heritage not connected)",
        title: "Heritage is not connected for this company. Open Edit Vendor for the Heritage vendor to connect.",
    };
}


export interface IPurchaseOrderItem {
    item_id: number | null;
    inventory_item_id: number | null;
    vendor_item_id: number | null;
    vendor_part_number: string | null;
    vendor_id: number | null;
    item_name: string;
    quantity: number;
    price: number | null;
    latest_status: string | null;
    last_update: string | null;
    vendor: string | null;
    is_heritage_vendor?: boolean | null;
    is_heritage_item?: boolean | null;
    catalog_branch_code?: string | null;
    catalog_branch_name?: string | null;
    /** #37 — the branch this line was directed to on the review page. */
    line_branch_code?: string | null;
    line_branch_name?: string | null;
}

export interface IConnectionStatus {
    access_token: string;
    created_at: string;
    credentials_exist: boolean;
    branches: {
        id: string;
        branch_name: string;
        branch_code: string;
        is_default: boolean;
    }[];
}

export interface IBranch {
    id: string;
    branch_name: string;
    branch_code: string;
    is_default: boolean;
    ship_to_sequence_number: string;
    address_line1: string | null;
    branch_city_state_zip: string | null;
    branch_phone: string | null;
    business_hours: string | null;
}

interface IProps {
    orderId: number;
    sendViaEmail: boolean;
    setSendViaEmail: (value: boolean) => void;
    sendViaApi: boolean;
    setSendViaApi: (value: boolean) => void;
    emailTo?: string;
    onSuccess?: () => void;
    onClose?: () => void;
}

interface IBusiness {
    id: string;
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zipcode: string | null;
    default_timezone: string | null;
}

interface IInventoryManager {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    is_inventory_manager: string;
    phone: string | null;
}

const SendPurchaseOrder = ({
    orderId,
    sendViaEmail,
    setSendViaEmail,
    sendViaApi,
    setSendViaApi,
    emailTo,
    onSuccess,
    onClose,
}: IProps) => {
    const [isSending, setIsSending] = useState(false);
    const [isCheckingStock, setIsCheckingStock] = useState(false);
    const [unlinkedItems, setUnlinkedItems] = useState<IPurchaseOrderItem[]>([]);
    const [hasHeritageItems, setHasHeritageItems] = useState(false);
    const [stockWarning, setStockWarning] = useState<string | null>(null);
    const [confirmSendDespiteStock, setConfirmSendDespiteStock] = useState(false);
    // ESERV2-1450 — set when Heritage reports a line it will not sell at the
    // ordered branch. Unlike stockWarning there is no "Submit anyway": the
    // ERP would reject the order and never say so.
    const [sellabilityBlock, setSellabilityBlock] = useState<string | null>(null);

    // Check Heritage integration status
    const { data: heritageAuthStatus } = useHeritageAuthStatus();
    const isHeritageConnected = heritageAuthStatus?.connected ?? false;

    const { data, isLoading } = useQuery<{
        items: IPurchaseOrderItem[];
        connection: IConnectionStatus;
        branch: IBranch;
        branchSource?: "catalog" | "vendor" | "company_default";
        catalogBranch?: { catalog_branch_code?: string; catalog_branch_name?: string } | null;
        business: IBusiness;
        inventoryManager: IInventoryManager
    }>(
        ["po-items", orderId],
        () =>
            axios
                .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/purchase-order-items`, {
                    params: { orderId },
                })
                .then((res) => ({
                    items: res.data.items.map((item: any) => {
                        // Determine if item is heritage-linked
                        // An item is heritage if: vendor is heritage AND has vendor_part_number
                        const isHeritageVendor = item.is_heritage_vendor ?? false;
                        const hasVendorPartNumber = !!item.vendor_part_number;
                        const isHeritageItem = isHeritageVendor && hasVendorPartNumber;
                        
                        return {
                            item_id: item.item_id ? parseInt(item.item_id) : null,
                            vendor_item_id: item.vendor_item_id ? parseInt(item.vendor_item_id) : null,
                            vendor_id: item.vendor_id ? parseInt(item.vendor_id) : null,
                            item_name: item.item_name,
                            quantity: parseFloat(item.quantity),
                            price: item.price != null ? item.price / 100 : null,
                            latest_status: item.latest_status,
                            last_update: item.last_update,
                            vendor: item.vendor ?? null,
                            inventory_item_id: item.inventory_item_id ? parseInt(item.inventory_item_id) : null,
                            vendor_part_number: item.vendor_part_number ?? null,
                            is_heritage_vendor: isHeritageVendor,
                            is_heritage_item: isHeritageItem,
                            catalog_branch_code: item.catalog_branch_code ?? null,
                            catalog_branch_name: item.catalog_branch_name ?? null,
                            line_branch_code: item.line_branch_code ?? null,
                            line_branch_name: item.line_branch_label ?? null,
                        };
                    }),
                    connection: res.data.connection,
                    branch: res.data.branch,
                    branchSource: res.data.branchSource ?? "company_default",
                    catalogBranch: res.data.catalogBranch ?? null,
                    business: res.data.business,
                    inventoryManager: res.data.inventoryManager,
                })),
        {
            enabled: !!orderId, // Always fetch when orderId is available, so we know heritage status before modal opens
        }
    );

    useEffect(() => {
        if (data?.items) {
            const items = data.items;
            // For heritage items, check if they have vendor_part_number
            // For non-heritage items, they don't need vendor_part_number
            const heritageItems = items.filter(item => item.is_heritage_item);
            const unlinkedHeritage = heritageItems.filter(item => !item.vendor_part_number);
            setUnlinkedItems(unlinkedHeritage);
            
            // Update hasHeritageItems state
            setHasHeritageItems(heritageItems.length > 0);
            
            // Auto-disable API if Heritage is not connected or there are no heritage items
            if ((!isHeritageConnected || heritageItems.length === 0) && sendViaApi) {
                setSendViaApi(false);
                // Auto-enable email if API was disabled and there are non-heritage items
                const nonHeritageItems = items.filter(item => !item.is_heritage_item);
                if (nonHeritageItems.length > 0) {
                    setSendViaEmail(true);
                }
            }
        } else {
            // Reset when data is not available
            setHasHeritageItems(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, sendViaApi, isHeritageConnected]);

    const toggleEmail = () => {
        if (!sendViaEmail) {
            // If turning email on, turn API off
            setSendViaEmail(true);
            setSendViaApi(false);
        } else {
            // If turning email off, keep both off (user must select one)
            setSendViaEmail(false);
        }
    };

    const toggleApi = () => {
        if (!sendViaApi) {
            setSendViaApi(true);
            setSendViaEmail(false);
        } else {
            setSendViaApi(false);
        }
        setStockWarning(null);
        setConfirmSendDespiteStock(false);
    };

    const heritageItemsOnOrder = data?.items?.filter((item) => item.is_heritage_item) ?? [];
    const heritageVendorName =
        heritageItemsOnOrder.find((item) => item.vendor?.trim())?.vendor?.trim() ?? "this Heritage vendor";

    const catalogSendBranch = resolveSendBranch(
        heritageItemsOnOrder,
        data?.branch ?? null,
        data?.branchSource
    );
    const catalogBranchReady = catalogSendBranch.status === "ready";
    // #37 — one Heritage order per branch; more than one group means this
    // send will place several orders, which the user should know beforehand.
    const plannedBranchGroups = catalogBranchReady
        ? groupSendBranches(heritageItemsOnOrder, {
              branchCode: catalogSendBranch.branchCode,
              branchName: catalogSendBranch.branchName,
          })
        : [];

    const handleSend = async () => {
        // Ensure at least one method is selected
        if (!sendViaEmail && !sendViaApi) {
            toast.error("Please select a sending method (Email or API)");
            return;
        }

        // If we don't have data yet, wait for it to load
        if (!data?.items) {
            if (isLoading) {
                toast.info("Loading items...");
                return;
            }
            toast.error("No items to send");
            return;
        }

        // Separate heritage and non-heritage items
        const heritageItems = data.items.filter(item => item.is_heritage_item);
        const nonHeritageItems = data.items.filter(item => !item.is_heritage_item);
        
        // If no items at all, show error
        if (heritageItems.length === 0 && nonHeritageItems.length === 0) {
            toast.error("No items to send");
            return;
        }

        // Check for unlinked heritage items (they need vendor_part_number)
        const unlinkedHeritage = heritageItems.filter(item => !item.vendor_part_number);
        if (unlinkedHeritage.length > 0 && sendViaApi) {
            toast.warn(`Cannot send: The following heritage items are not linked (missing vendor part number): ${unlinkedHeritage.map(i => i.item_name).join(", ")}. Link them in Vendors.`);
            return;
        }

        if (sendViaApi && heritageItems.length > 0 && !catalogBranchReady) {
            if (catalogSendBranch.status === "mixed") {
                toast.error(
                    "Heritage items on this order have different catalog branches (or some have none). Split the PO or refresh the catalog in Edit Vendor."
                );
            } else {
                toast.error(
                    "No Heritage branch is configured for this vendor. Select a branch in Edit Vendor before submitting to Heritage."
                );
            }
            return;
        }

        const resolvedSendBranchCode = catalogSendBranch.branchCode;

        // Live stock check at send time (not stored during catalog download).
        if (sendViaApi && heritageItems.length > 0 && !confirmSendDespiteStock) {
            setIsCheckingStock(true);
            try {
                const heritageVendorId = heritageItems.find((item) => item.vendor_id)?.vendor_id ?? null;
                // #37 — the API places one Heritage order per branch, so the
                // pre-send stock check runs the same way: each group against
                // the branch its lines will actually ship from.
                const stockGroups = groupSendBranches(heritageItems, {
                    branchCode: resolvedSendBranchCode,
                    branchName: catalogSendBranch.branchName,
                });
                const stockIssues: string[] = [];
                const blockingIssues: string[] = [];
                setSellabilityBlock(null);
                for (const group of stockGroups) {
                    const availRes = await axios.post(
                        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/check-availability`,
                        {
                            orderId,
                            vendorId: heritageVendorId,
                            branchCode: group.branchCode,
                            items: group.items.map(item => ({
                                heritagePartNumber: item.vendor_part_number,
                                vendor_part_number: item.vendor_part_number,
                                customerPartNumber: item.vendor_part_number || item.item_name,
                                catalog_branch_code: item.catalog_branch_code ?? null,
                                item_name: item.item_name,
                                quantity: item.quantity,
                                item_id: item.item_id,
                                vendor_id: item.vendor_id,
                                uom: "EA",
                            })),
                        }
                    );

                    const branchLabel = group.branchName || group.branchCode || "";
                    const { blocking, warnings } = partitionStockResults(availRes.data.results, branchLabel);
                    blockingIssues.push(...blocking);
                    stockIssues.push(...warnings);
                }

                if (blockingIssues.length > 0) {
                    const text = sellabilityBlockMessage(blockingIssues);
                    setSellabilityBlock(text);
                    setStockWarning(null);
                    setConfirmSendDespiteStock(false);
                    setIsCheckingStock(false);
                    toast.error(text);
                    return;
                }

                if (stockIssues.length > 0) {
                    setStockWarning(
                        `Stock: ${stockIssues.join("; ")}. Click Submit anyway to send it to Heritage regardless.`
                    );
                    setConfirmSendDespiteStock(true);
                    setIsCheckingStock(false);
                    return;
                }
            } catch (err) {
                // An unusable Heritage login is not a stock hiccup: block, no
                // "Submit anyway" — /send would fail on the same login (ESERV2-1452).
                if (isHeritageCredentialsError(err)) {
                    setSellabilityBlock(credentialsBlockMessage());
                    setIsCheckingStock(false);
                    return;
                }
                const apiMessage = heritageApiErrorMessage(err);
                setStockWarning(
                    apiMessage
                        ? `Could not verify stock with Heritage: ${apiMessage} Click Submit anyway to submit the order.`
                        : "Could not verify stock with Heritage. Click Submit anyway to submit the order."
                );
                setConfirmSendDespiteStock(true);
                setIsCheckingStock(false);
                return;
            }
            setIsCheckingStock(false);
        }

        setIsSending(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/send`,
                {
                    orderId,
                    sendViaEmail,
                    sendViaApi,
                    heritageBranchCode: sendViaApi ? resolvedSendBranchCode : undefined,
                    emailTo: sendViaEmail ? emailTo : undefined,
                    heritageItems: heritageItems.map(item => ({
                        item_id: item.item_id,
                        inventory_item_id: item.inventory_item_id,
                        vendor_item_id: item.vendor_item_id,
                        vendor_part_number: item.vendor_part_number,
                        vendor_id: item.vendor_id,
                        item_name: item.item_name,
                        quantity: item.quantity,
                        price: item.price,
                    })),
                    nonHeritageItems: nonHeritageItems.map(item => ({
                        item_id: item.item_id,
                        inventory_item_id: item.inventory_item_id,
                        vendor_item_id: item.vendor_item_id,
                        vendor_part_number: item.vendor_part_number,
                        vendor_id: item.vendor_id,
                        item_name: item.item_name,
                        quantity: item.quantity,
                        price: item.price,
                    })),
                }
            );

            const responseData = response.data;

            if (responseData.success) {
                toast.success("Purchase order sent successfully");
                setStockWarning(null);
                setConfirmSendDespiteStock(false);
                if (onSuccess) onSuccess();
            } else {
                // Handle error response with detailed error messages
                let errorMessage = "Failed to send purchase order";
                
                if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                    const notSellable = responseData.errors.find(
                        (err: { code?: string }) => err?.code === "HERITAGE_NOT_SELLABLE"
                    );
                    if (notSellable) {
                        setSellabilityBlock(notSellable.error);
                    }
                    const errorDetails = responseData.errors.map((err: any) => {
                        if (err.type === 'heritage') {
                            return `Heritage: ${err.error}`;
                        } else if (err.type === 'email') {
                            return `Email: ${err.error}`;
                        }
                        return err.error || err;
                    }).join('; ');
                    
                    errorMessage += ` - ${errorDetails}`;
                }
                
                toast.error(errorMessage);
            }
        } catch (error: any) {
            // Handle axios errors
            let errorMessage = "Error sending purchase order";
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                if (errorData.message) {
                    errorMessage = errorData.message;
                }
                
                if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                    const errorDetails = errorData.errors.map((err: any) => {
                        if (err.type === 'heritage') {
                            return `Heritage: ${err.error}`;
                        } else if (err.type === 'email') {
                            return `Email: ${err.error}`;
                        }
                        return err.error || err;
                    }).join('; ');
                    
                    errorMessage += ` - ${errorDetails}`;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
            console.error("Purchase order send error:", error);
        } finally {
            setIsSending(false);
        }
    };


    return (
        <div className="bg-white p-4 rounded shadow-md w-full max-w-md text-sm">

            <div className="flex justify-between items-start mb-1">
                <h3 className="text-lg font-semibold">Send Purchase Order</h3>

            </div>

            <div className="flex justify-between items-center mb-2">
                <span>Send via Email</span>
                <button
                    className={clsx(
                        "w-10 h-5 rounded-full flex items-center px-1 transition-colors",
                        sendViaEmail ? "bg-green-500" : "bg-gray-300"
                    )}
                    onClick={toggleEmail}
                >
                    <div
                        className={clsx(
                            "bg-white w-4 h-4 rounded-full shadow transition-transform",
                            sendViaEmail ? "translate-x-5" : "translate-x-0"
                        )}
                    />
                </button>
            </div>

            <div className="flex justify-between items-center mb-2">
                <span>Submit to Heritage</span>
                <div className="flex items-center gap-2">
                    {(() => {
                        const isDisabled = !isHeritageConnected || !hasHeritageItems || isLoading;
                        let disabledMessage = "";
                        
                        if (isLoading) {
                            disabledMessage = "(Loading...)";
                        } else if (!isHeritageConnected) {
                            disabledMessage = heritageSendApiDisabledHint(heritageAuthStatus).message;
                        } else if (!hasHeritageItems) {
                            disabledMessage = "(No heritage items)";
                        }
                        
                        return (
                            <>
                                {isDisabled && disabledMessage && (
                                    <span className="text-xs text-gray-500">
                                        {disabledMessage}
                                    </span>
                                )}
                                <button
                                    className={clsx(
                                        "w-10 h-5 rounded-full flex items-center px-1 transition-colors",
                                        sendViaApi ? "bg-green-500" : "bg-gray-300",
                                        isDisabled && "opacity-50 cursor-not-allowed"
                                    )}
                                    onClick={toggleApi}
                                    disabled={isDisabled}
                                    title={
                                        !isHeritageConnected
                                            ? heritageSendApiDisabledHint(heritageAuthStatus).title
                                            : !hasHeritageItems 
                                                ? "No heritage items in this purchase order"
                                                : isLoading 
                                                    ? "Loading purchase order items..."
                                                    : ""
                                    }
                                >
                                    <div
                                        className={clsx(
                                            "bg-white w-4 h-4 rounded-full shadow transition-transform",
                                            sendViaApi ? "translate-x-5" : "translate-x-0"
                                        )}
                                    />
                                </button>
                            </>
                        );
                    })()}
                </div>
            </div>

            {sendViaApi && (
                <div className="mt-3 space-y-2">
                    {catalogSendBranch.status === "ready" ? (
                        plannedBranchGroups.length > 1 ? (
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-900 m-0">
                                    This order will be submitted as {plannedBranchGroups.length} Heritage
                                    orders — one per branch:
                                </p>
                                <ul className="m-0 pl-4 list-disc space-y-0.5 text-xs text-gray-600">
                                    {plannedBranchGroups.map((group) => (
                                        <li key={group.branchCode}>
                                            {group.branchName || group.branchCode} ({group.branchCode}):{" "}
                                            {group.items.map((i) => i.item_name).join(", ")}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                        <>
                            <p className="text-sm font-medium text-gray-900 m-0">
                                Sending from:{" "}
                                {catalogSendBranch.branchName || catalogSendBranch.branchCode} (
                                {catalogSendBranch.branchCode})
                            </p>
                            <p className="text-xs text-gray-500 m-0">
                                {catalogSendBranch.source === "catalog"
                                    ? HERITAGE_SEND_CATALOG_BRANCH_HINT
                                    : "From this vendor's Heritage branch settings — purchase-history items carry no catalog branch."}
                            </p>
                        </>
                        )
                    ) : catalogSendBranch.status === "unavailable" ? (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1"
                            role="status"
                        >
                            <p className="m-0 font-medium">No Heritage branch configured</p>
                            <p className="m-0">
                                {HERITAGE_REFRESH_STOCK_BRANCH_SEND_MISSING.replace(
                                    "this Heritage vendor",
                                    heritageVendorName
                                )}
                            </p>
                        </div>
                    ) : (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1"
                            role="alert"
                        >
                            <p className="m-0 font-medium">Mixed catalog branches on this order</p>
                            <p className="m-0">
                                Lines without a branch of their own must share one catalog branch. Assign each
                                line a branch on the Review page (one Heritage order is placed per branch), or
                                split this PO.
                            </p>
                            <ul className="m-0 pl-4 list-disc space-y-0.5">
                                {heritageItemsOnOrder.map((item) =>
                                    item.catalog_branch_code ? (
                                        <li key={`${item.item_id}-${item.vendor_part_number}`}>
                                            {item.item_name}: {item.catalog_branch_name || item.catalog_branch_code}{" "}
                                            ({item.catalog_branch_code})
                                        </li>
                                    ) : (
                                        <li key={`${item.item_id}-${item.vendor_part_number}`}>
                                            {item.item_name}: no catalog branch on file
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {sellabilityBlock && (
                <div
                    role="alert"
                    data-testid="sellability-block"
                    className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
                >
                    {sellabilityBlock}
                </div>
            )}

            {stockWarning && (
                <div
                    role="alert"
                    className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                    {stockWarning}
                </div>
            )}


            <div className="flex justify-end gap-2 pt-4 mt-4">
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            setSendViaApi(false);
                            setSendViaEmail(false);
                            setStockWarning(null);
                            setConfirmSendDespiteStock(false);
                            setSellabilityBlock(null);
                            if (onClose) onClose();
                        }}
                        className="px-4 py-1.5 rounded border text-gray-700 hover:bg-gray-100"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSend}
                        disabled={
                            isSending ||
                            (sendViaApi && sellabilityBlock != null) ||
                            (!sendViaEmail && !sendViaApi) ||
                            (sendViaApi && unlinkedItems.length > 0) ||
                            (sendViaApi && !catalogBranchReady)
                        }
                        className="px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {sendPurchaseOrderActionLabel({
                            sendViaApi,
                            isCheckingStock,
                            isSending,
                            confirmSendDespiteStock,
                        })}
                    </button>
                </div>
            </div>

        </div>
    );
}

export default SendPurchaseOrder;